<script setup lang="ts">
import { NuxtLink } from "#components";

import type { CardStoryblok } from "~/storyblok/.generated/component-types";

const props = defineProps<{
  title?: string;
  description?: string;
  image?: CardStoryblok["image"];
  link?: string;
  linkText?: string;
  position?: number;
}>();

const rawLink = computed(() => props.link ?? "");

const { isExternalLink } = useLocaleOrExternalPath(rawLink);
const { getImgixUrlFromStoryblokMedia } = useImgixUrlFromStoryblokMedia();

const localePath = useLocalePath();

const linkProperties = computed(() => {
  if (!props.link) return {};

  return {
    href: isExternalLink.value ? props.link : localePath(props.link),
    target: isExternalLink.value ? "_blank" : undefined,
  };
});

const { cardBlockClicked } = useAnalytics();

const trackCardClicked = () => {
  if (!props.link) return;

  cardBlockClicked({
    card_position: props.position,
    redirected_url: props.link,
  });
};
</script>

<template>
  <component
    :is="link ? NuxtLink : 'div'"
    class="card__block group base-block-spacing"
    v-bind="linkProperties"
    @click="trackCardClicked"
  >
    <div
      v-if="image?.filename"
      class="card__block--image__container"
    >
      <NuxtImg
        provider="imgix"
        :src="getImgixUrlFromStoryblokMedia(image?.filename)"
        :alt="image?.alt ?? 'card image'"
        preset="card"
        loading="lazy"
      />
    </div>
    <div class="card__block--text__container">
      <h2
        v-if="title"
        class="uppercase text-lg font-bold"
      >
        {{ title }}
      </h2>
      <p v-if="description">
        {{ description }}
      </p>
      <p
        v-if="link && linkText"
        class="flex items-center gap-1 transition-all text-base font-bold cursor-pointer uppercase group-hover:gap-3"
      >
        {{ linkText }}
        <BaseIcon
          name="arrowRight"
          :size="16"
        />
      </p>
    </div>
  </component>
</template>

<style lang="postcss" scoped>
.card__block {
  @apply flex flex-col flex-1;
  @apply w-full;
  &--image__container {
    @apply flex justify-center;
    @apply w-full md:h-[450px];
    @apply overflow-hidden;
    img {
      @apply w-full;
      @apply object-cover;
    }
  }
  &--text__container {
    @apply flex flex-col gap-4 sm:gap-2;
    @apply pt-3 sm:pt-2;
  }
}
</style>
